import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { toEnglishDigits, toPersianDigits } from '@bookora/shared';
import { OTPInput, REGEXP_ONLY_DIGITS } from 'input-otp';
/**
 * `REGEXP_ONLY_DIGITS`, widened by the two digit sets an Iranian keyboard
 * actually produces.
 *
 * `input-otp`'s own constant is `^\d+$`, and `\d` in JavaScript is `[0-9]` and
 * nothing else. The library tests the pattern **before** calling `onChange`, so
 * with the stock constant a keystroke of «۱» is rejected inside the library and
 * our normalisation never runs: an Iranian user with a Persian keyboard cannot
 * type the code at all. Nothing surfaces — no error, no character, the cell just
 * stays empty — which is the worst possible failure for a login screen.
 *
 * So the pattern is widened here and the value is folded to ASCII in
 * `onChange`. The field still accepts nothing but digits; it simply accepts the
 * digits this product's users have on their keyboards. A test types «۱۲۳۴۵» and
 * asserts `12345` comes out.
 *
 * Derived from the library's constant rather than written out, so that a change
 * to `REGEXP_ONLY_DIGITS` upstream breaks the assertion in `otp-input.test.tsx`
 * instead of quietly producing a pattern that no longer means what it says.
 */
export const OTP_DIGIT_PATTERN = REGEXP_ONLY_DIGITS.replace('\\d', '[0-9۰-۹٠-٩]');
/**
 * The ref is the library's real `<input>` — the one holding the caret. A host
 * that suppresses `autoFocus` to avoid opening the keyboard mid-animation
 * focuses it itself once the step has settled.
 */
export const OtpInput = forwardRef(function OtpInput({ value = '', onChange, onComplete, length = 5, autoFocus = true, disabled = false, invalid = false, ariaLabel, className, id, }, ref) {
    const label = ariaLabel ?? `کد ${toPersianDigits(length)} رقمی پیامک‌شده`;
    return (_jsx("div", { dir: "ltr", className: ['bk-otp', className].filter(Boolean).join(' '), children: _jsx(OTPInput, { ref: ref, id: id, dir: "ltr", maxLength: length, value: value, onChange: (next) => onChange?.(toEnglishDigits(next).replace(/\D/g, '')), onComplete: (next) => onComplete?.(toEnglishDigits(next).replace(/\D/g, '')), pattern: OTP_DIGIT_PATTERN, 
            // Runs before the pattern test. Users paste the whole SMS, not just the
            // code, so any digit run in the pasted text is taken.
            pasteTransformer: (pasted) => {
                const ascii = toEnglishDigits(pasted);
                return (new RegExp(`\\d{${length}}`).exec(ascii)?.[0] ?? ascii.replace(/\D/g, '')).slice(0, length);
            }, autoFocus: autoFocus, disabled: disabled, "aria-label": label, "aria-invalid": invalid || undefined, autoComplete: "one-time-code", inputMode: "numeric", textAlign: "center", 
            // 16px on the library's hidden <input>. Anything smaller makes iOS
            // Safari zoom the viewport on focus and never zoom back out.
            className: "bk-otp__field", containerClassName: "bk-otp__cells", render: ({ slots }) => (_jsx(_Fragment, { children: slots.map((slot, index) => (_jsx(OtpCell, { slot: slot, invalid: invalid, disabled: disabled }, index))) })) }) }));
});
function OtpCell({ slot, invalid, disabled, }) {
    const { char, isActive, hasFakeCaret } = slot;
    return (_jsxs("div", { className: "bk-otp__cell", "data-filled": char ? 'true' : undefined, "data-active": isActive ? 'true' : undefined, "data-invalid": invalid ? 'true' : undefined, "data-disabled": disabled ? 'true' : undefined, "aria-hidden": "true", children: [char ? toPersianDigits(char) : null, hasFakeCaret ? _jsx("span", { className: "bk-otp__caret" }) : null] }));
}
//# sourceMappingURL=otp-input.js.map